import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MoreHorizontal } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserFormDialog } from "@/components/admin/UserFormDialog";
import { UserPermissionsDialog, ResetPasswordDialog } from "@/components/admin/UserDialogs";
import { RolesPanel } from "@/components/admin/RolesPanel";
import { LoginHistory, ActivityLog, SessionsPanel } from "@/components/admin/AdminLogs";
import { WelcomeCardDialog } from "@/components/admin/WelcomeCardDialog";
import { cn } from "@/lib/utils";

const ST = { Active: "bg-emerald-500/10 text-emerald-500", Suspended: "bg-amber-500/10 text-amber-500", Disabled: "bg-secondary text-muted-foreground" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, user: null });
  const [permUser, setPermUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [view, setView] = useState(null);
  const [card, setCard] = useState(null);

  const loadMeta = useCallback(() => api.get("/admin/meta").then(({ data }) => setMeta(data)).catch(() => {}), []);
  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}), []);
  useEffect(() => { loadMeta(); load(); }, [loadMeta, load]);

  const openPerms = async (u) => { const { data } = await api.get(`/admin/users/${u.id}`); setPermUser(data); };
  const setStatus = async (u, status) => {
    try { await api.post(`/admin/users/${u.id}/status`, { status }); toast.success(`${u.name} is now ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const revokeAll = async (u) => {
    try { await api.post(`/admin/users/${u.id}/revoke-sessions`); toast.success(`All sessions for ${u.name} revoked`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const ql = q.toLowerCase();
  const shown = users.filter((u) => !q || [u.name, u.login_id, u.email, u.role, u.mobile].join(" ").toLowerCase().includes(ql));

  return (
    <div data-testid="admin-users-page">
      <PageHeader title="User & Role Management" subtitle="Create staff logins, assign roles, fine-tune permissions and restrict access by location, franchise or DP." />
      <Tabs defaultValue="users">
        <TabsList className="h-auto flex-wrap gap-1">
          <TabsTrigger value="users" data-testid="tab-users">Manage Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="logins" data-testid="tab-login-history">Login History</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">User Activity Logs</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Active Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Input className="max-w-xs" placeholder="Search name, login ID, role…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
            <Button onClick={() => setForm({ open: true, user: null })} data-testid="add-user-btn"><Plus className="mr-1 h-4 w-4" /> Add New User</Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Login ID</TableHead><TableHead>Role</TableHead><TableHead>Access Scope</TableHead>
                <TableHead>Status</TableHead><TableHead>Last Login</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {shown.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.login_id || u.email}`}>
                    <TableCell><p className="font-semibold">{u.name}</p><p className="text-xs text-muted-foreground">{u.designation || u.email}</p></TableCell>
                    <TableCell className="font-mono text-xs">{u.login_id || u.email}</TableCell>
                    <TableCell className="text-sm">{u.role}</TableCell>
                    <TableCell className="text-xs">{u.scope_label}</TableCell>
                    <TableCell><span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", ST[u.status])} data-testid={`user-status-${u.login_id || u.email}`}>{u.status}</span></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleString("en-IN") : "Never"}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`user-actions-${u.login_id || u.email}`}>Manage <MoreHorizontal className="ml-1 h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setView(u)} data-testid="ua-view">View User</DropdownMenuItem>
                          {u.login_id && <DropdownMenuItem onClick={() => setCard(u)} data-testid="ua-welcome-card">Welcome Card</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => setForm({ open: true, user: u })} data-testid="ua-edit">Edit User</DropdownMenuItem>
                          {u.id !== me?.id && u.role !== "Super Admin" && <DropdownMenuItem onClick={() => openPerms(u)} data-testid="ua-perms">Manage Permissions</DropdownMenuItem>}
                          {u.id !== me?.id && <DropdownMenuItem onClick={() => setPwUser(u)} data-testid="ua-reset">Reset Password</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          {u.id !== me?.id && u.status === "Active" && <DropdownMenuItem onClick={() => setStatus(u, "Suspended")} className="text-amber-600" data-testid="ua-suspend">Suspend User</DropdownMenuItem>}
                          {u.id !== me?.id && u.status !== "Active" && <DropdownMenuItem onClick={() => setStatus(u, "Active")} className="text-emerald-600" data-testid="ua-activate">Activate User</DropdownMenuItem>}
                          {u.id !== me?.id && u.status !== "Disabled" && <DropdownMenuItem onClick={() => setStatus(u, "Disabled")} data-testid="ua-disable">Disable User</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => revokeAll(u)} data-testid="ua-revoke">Revoke Sessions ({u.active_sessions})</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setView({ ...u, tab: "activity" })} data-testid="ua-activity">View Activity Log</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="roles" className="mt-4">{meta && <RolesPanel meta={meta} reload={loadMeta} />}</TabsContent>
        <TabsContent value="logins" className="mt-4"><LoginHistory /></TabsContent>
        <TabsContent value="activity" className="mt-4"><ActivityLog /></TabsContent>
        <TabsContent value="sessions" className="mt-4"><SessionsPanel /></TabsContent>
      </Tabs>

      <UserFormDialog open={form.open} user={form.user} meta={meta} onClose={() => setForm({ open: false, user: null })} onSaved={(created) => { load(); if (created) setCard(created); }} />
      <WelcomeCardDialog user={card} onClose={() => setCard(null)} />
      <UserPermissionsDialog user={permUser} meta={meta} onClose={() => setPermUser(null)} onSaved={load} />
      <ResetPasswordDialog user={pwUser} onClose={() => setPwUser(null)} />
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto bg-card" data-testid="user-view-dialog">
          <DialogHeader><DialogTitle className="font-head">{view?.name} · {view?.role}</DialogTitle></DialogHeader>
          {view && <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            {[["Login ID", view.login_id || "-"], ["Email", view.email || "-"], ["Mobile", view.mobile || "-"], ["Designation", view.designation || "-"],
              ["Status", view.status], ["Access Scope", view.scope_label], ["Created By", view.created_by || "-"], ["Active Sessions", view.active_sessions]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-border p-2"><p className="text-[11px] text-muted-foreground">{k}</p><p className="font-medium">{v}</p></div>))}
          </div>}
          {view && <Tabs defaultValue={view.tab || "activity"} className="mt-3">
            <TabsList><TabsTrigger value="activity">Activity</TabsTrigger><TabsTrigger value="logins">Logins</TabsTrigger><TabsTrigger value="sessions">Sessions</TabsTrigger></TabsList>
            <TabsContent value="activity"><ActivityLog userId={view.id} /></TabsContent>
            <TabsContent value="logins"><LoginHistory userId={view.id} /></TabsContent>
            <TabsContent value="sessions"><SessionsPanel userId={view.id} /></TabsContent>
          </Tabs>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
